<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api, ApiError } from '../api/client'
import PageBody from '../components/PageBody.vue'
import PageHeader from '../components/PageHeader.vue'
import { IconAlert, IconKey, IconTrash } from '../components/Icons'
import Modal from '../components/Modal.vue'
import { useToast } from '../lib/toast'
import { Badge, Button, DateTimeField, EmptyState, ErrorState, Field, Panel, SkeletonRows, Textarea } from '../components/ui'
import { formatDateTime, fromLocalInput, lineState, shortID, toLocalInput } from '../lib/format'
import { useAsync, useTitle } from '../lib/hooks'

const EXPIRY_TONE = { permanent: 'ok', active: 'ok', expiring: 'warn', expired: 'danger', none: 'neutral' } as const
const LINE_STATUS = { pending: '开通中', active: '生效中', expired: '已过期', disabled: '已停用' } as const

const route = useRoute()
const router = useRouter()
const toast = useToast()
useTitle('账号详情')

const id = computed(() => String(route.params.id ?? ''))
const { data, error, reload } = useAsync(() => api.userDetail(id.value), () => [id.value])
const notFound = computed(() => error.value?.status === 404)

const expires = ref('')
const note = ref('')
const formError = ref('')
const busy = ref(false)
const confirmRevoke = ref(false)

function prefillExpiry(iso: string | null | undefined) {
  if (!iso) return ''
  return new Date(iso).getTime() > Date.now() ? toLocalInput(iso) : ''
}

watch(
  () => data.value,
  (user) => {
    if (!user) return
    expires.value = prefillExpiry(user.line?.expire_at)
    note.value = ''
    formError.value = ''
  },
  { immediate: true },
)

const line = computed(() => data.value?.line ?? null)
const info = computed(() => (line.value ? lineState(line.value.status, line.value.expire_at) : null))

const currentExpiry = computed(() => {
  if (!line.value) return '尚未开通'
  if (!line.value.expire_at) return '永久'
  return formatDateTime(line.value.expire_at)
})

const expiryPast = computed(() => {
  const at = line.value?.expire_at
  if (!at) return line.value?.status === 'expired'
  const t = new Date(at).getTime()
  return Number.isNaN(t) ? false : t <= Date.now()
})

async function submit() {
  const user = data.value
  if (!user) return
  const iso = fromLocalInput(expires.value)
  if (!iso || new Date(iso).getTime() <= Date.now()) {
    formError.value = '到期时间必须晚于当前时间'
    return
  }

  busy.value = true
  formError.value = ''
  try {
    await api.grantEntitlement(user.id, { expires_at: iso, note: note.value.trim() })
    reload()
    toast.ok('线路权益已更新')
  } catch (err) {
    formError.value = err instanceof ApiError ? err.message : '保存失败'
  } finally {
    busy.value = false
  }
}

async function revoke() {
  const user = data.value
  if (!user) return
  busy.value = true
  try {
    await api.revokeEntitlement(user.id)
    confirmRevoke.value = false
    reload()
    toast.ok('线路已停用')
  } catch (err) {
    toast.fail(err instanceof ApiError ? err.message : '停用失败')
  } finally {
    busy.value = false
  }
}

function resetForm() {
  expires.value = prefillExpiry(line.value?.expire_at)
  note.value = ''
  formError.value = ''
}
</script>

<template>
  <template v-if="notFound">
    <PageHeader title="账号不存在" :breadcrumb="[{ label: '账号', to: '/users' }, { label: id }]" />
    <PageBody>
      <Panel>
        <EmptyState :title="`找不到账号 ${id}`" hint="账号可能已被删除。">
          <template #action>
            <Button size="sm" @click="router.push('/users')">返回账号列表</Button>
          </template>
        </EmptyState>
      </Panel>
    </PageBody>
  </template>

  <template v-else>
    <PageHeader
      :title="data?.email ?? id"
      :subtitle="data ? `注册于 ${formatDateTime(data.created_at)}` : undefined"
      :breadcrumb="[{ label: '账号', to: '/users' }, { label: data?.email ?? id }]"
    />
    <PageBody>
      <Panel v-if="error">
        <ErrorState :message="error.message" @retry="reload" />
      </Panel>
      <SkeletonRows v-else-if="!data" :rows="6" :cols="3" />
      <div v-else class="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
        <Panel title="账号信息">
          <dl class="divide-y divide-line">
            <div class="flex items-start justify-between gap-4 px-4 py-3">
              <dt class="pt-0.5 text-[13px] text-muted">账号 ID</dt>
              <dd class="min-w-0 text-right text-[14px]">
                <span class="tnum" :title="data.id">{{ shortID(data.id, 10, 6) }}</span>
              </dd>
            </div>
            <div class="flex items-start justify-between gap-4 px-4 py-3">
              <dt class="pt-0.5 text-[13px] text-muted">邮箱</dt>
              <dd class="min-w-0 text-right text-[14px]"><span class="break-all">{{ data.email }}</span></dd>
            </div>
            <div class="flex items-start justify-between gap-4 px-4 py-3">
              <dt class="pt-0.5 text-[13px] text-muted">角色</dt>
              <dd class="min-w-0 text-right text-[14px]">
                <Badge :tone="data.role === 'admin' ? 'info' : 'neutral'">
                  {{ data.role === 'admin' ? '管理员' : '用户' }}
                </Badge>
              </dd>
            </div>
            <div class="flex items-start justify-between gap-4 px-4 py-3">
              <dt class="pt-0.5 text-[13px] text-muted">邮箱验证</dt>
              <dd class="min-w-0 text-right text-[14px]">
                <Badge v-if="data.verified_at" tone="ok">已验证</Badge>
                <Badge v-else tone="warn">未验证</Badge>
              </dd>
            </div>
            <div class="flex items-start justify-between gap-4 px-4 py-3">
              <dt class="pt-0.5 text-[13px] text-muted">活跃会话</dt>
              <dd class="min-w-0 text-right text-[14px]"><span class="tnum">{{ data.active_sessions }}</span></dd>
            </div>
            <div class="flex items-start justify-between gap-4 px-4 py-3">
              <dt class="pt-0.5 text-[13px] text-muted">注册时间</dt>
              <dd class="min-w-0 text-right text-[14px]">
                <span class="tnum">{{ formatDateTime(data.created_at) }}</span>
              </dd>
            </div>
          </dl>
          <p
            v-if="!data.verified_at"
            class="flex items-start gap-2 border-t border-line px-4 py-3 text-[13px] leading-relaxed text-muted"
          >
            <IconAlert :size="14" class="mt-0.5 shrink-0 text-warn" />
            邮箱未验证的账号即使开通线路，App 端登录也会被拒。
          </p>
        </Panel>

        <div class="flex flex-col gap-4">
          <Panel title="播放线路">
            <EmptyState
              v-if="!line"
              title="尚未开通线路"
              hint="该账号登录后看不到可播放的线路。用下面的表单开通。"
            />
            <template v-else>
              <div class="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
                <span class="tnum text-[20px] font-semibold">{{ line.username }}</span>
                <Badge :tone="line.status === 'active' ? 'ok' : line.status === 'expired' ? 'danger' : 'neutral'">
                  {{ LINE_STATUS[line.status] }}
                </Badge>
                <Badge v-if="line.status === 'active' && info" :tone="EXPIRY_TONE[info.state]">{{ info.label }}</Badge>
              </div>
              <dl class="divide-y divide-line">
                <div class="flex items-start justify-between gap-4 px-4 py-3">
                  <dt class="pt-0.5 text-[13px] text-muted">线路 ID</dt>
                  <dd class="min-w-0 text-right text-[14px] break-all">{{ line.line_id || '—' }}</dd>
                </div>
                <div class="flex items-start justify-between gap-4 px-4 py-3">
                  <dt class="pt-0.5 text-[13px] text-muted">到期时间</dt>
                  <dd
                    class="min-w-0 text-right text-[14px] break-words"
                    :class="expiryPast ? 'font-medium text-danger' : 'text-text'"
                  >
                    {{ formatDateTime(line.expire_at) }}
                  </dd>
                </div>
                <div class="flex items-start justify-between gap-4 px-4 py-3">
                  <dt class="pt-0.5 text-[13px] text-muted">Bouquet</dt>
                  <dd class="min-w-0 text-right text-[14px] break-words">
                    {{ line.bouquets.length ? line.bouquets.join(', ') : '—' }}
                  </dd>
                </div>
              </dl>
            </template>
          </Panel>

          <Panel
            :title="line ? '调整权益' : '开通权益'"
            hint="保存后同步播放面板和登录看到的到期时间。已有线路只改这一条，不会另开新线。"
          >
            <template v-if="line && line.status !== 'disabled'" #actions>
              <Button size="sm" variant="danger" @click="confirmRevoke = true">
                <IconTrash :size="13" />
                停用
              </Button>
            </template>
            <div class="flex flex-col gap-4 px-4 py-4">
              <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div class="min-w-0">
                  <p class="text-[13px] text-muted">当前到期</p>
                  <p
                    class="mt-1 text-[20px] font-semibold tracking-tight"
                    :class="expiryPast ? 'text-danger' : 'text-text'"
                  >
                    {{ currentExpiry }}
                  </p>
                </div>
                <DateTimeField v-model="expires" class="sm:w-[260px]" />
              </div>
              <Field label="备注" hint="只写入审计日志">
                <Textarea v-model="note" :rows="2" placeholder="例如：客服补开到 11 月" />
              </Field>
              <p
                v-if="formError"
                role="alert"
                class="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-[12.5px] text-danger"
              >
                {{ formError }}
              </p>
              <div class="flex items-center gap-2">
                <Button variant="primary" :loading="busy" @click="submit">
                  <IconKey :size="13" />
                  {{ line ? '保存权益' : '开通权益' }}
                </Button>
                <Button @click="resetForm">重置</Button>
                <RouterLink to="/users" class="ml-auto text-[14px] text-link hover:underline">返回列表</RouterLink>
              </div>
            </div>
          </Panel>

          <Modal
            :open="confirmRevoke"
            title="停用该账号的线路？"
            hint="面板到期时间会改成现在，登录不再下发播放凭证。之后可以用同一表单重新开通。"
            @close="confirmRevoke = false"
          >
            <template #footer>
              <Button @click="confirmRevoke = false">取消</Button>
              <Button variant="danger" :loading="busy" @click="revoke">确认停用</Button>
            </template>
            <p class="text-[13px] leading-relaxed text-muted">
              停用 <span class="text-text">{{ data.email }}</span> 的线路
              <span v-if="line" class="tnum text-text">{{ line.username }}</span>。
            </p>
          </Modal>
        </div>
      </div>
    </PageBody>
  </template>
</template>
